import { createFileRoute, notFound, redirect, useRouter } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";

import { Footer, Wordmark } from "../components/lidoo/chrome";
import { PhotoUpload } from "../components/lidoo/photo-upload";
import { LANGS, OWNER_LANG_BY_COUNTRY, useI18n, type Lang } from "../lib/i18n";
import {
  addAvailabilityBlock,
  addClubBooking,
  removeClubBooking,
  getClubManageView,
  listAvailabilityBlocks,
  removeAvailabilityBlock,
  setRequestStatus,
  setReservationStatus,
  updateClubDetails,
  type ManageRequest,
  type ManageReservation,
} from "../lib/api/lidoo.functions";

export const Route = createFileRoute("/manage/$token")({
  loader: async ({ params, location }) => {
    // A short or malformed token used to fail validation and surface as a 500.
    // Mail clients wrap and truncate long URLs constantly, so for the owner on
    // the other end that is the single most likely thing to go wrong. Only the
    // lookup is guarded: the language redirect below is control flow, not an
    // error, and must not be swallowed by this catch.
    let res;
    try {
      res = await getClubManageView({ data: { token: params.token } });
    } catch {
      throw notFound();
    }
    if (!res.ok) throw notFound();

    // Land the owner in the language of their own country, once. It lives in
    // the URL so forwarding the link to a colleague keeps the choice.
    const search = location.search as { lang?: string };
    if (!search.lang) {
      throw redirect({
        to: "/manage/$token",
        params: { token: params.token },
        search: { lang: OWNER_LANG_BY_COUNTRY[res.club.country] ?? "en" },
      });
    }

    const blocks = await listAvailabilityBlocks({ data: { token: params.token } });
    return { ...res, blocks: blocks.ok ? blocks.blocks : [] };
  },
  notFoundComponent: BrokenLink,
  // A club's own bookings and its guests' contact details must never reach a
  // search index, and the page is behind an unguessable token anyway.
  head: () => ({
    meta: [
      { title: "Manage your club | sunbedly" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: ManagePage,
});

// Shown when the link is wrong, which in practice means an email client broke
// it. Says so plainly and gives the one action that fixes it, rather than
// leaving an owner thinking the whole thing is broken.
function BrokenLink() {
  return (
    <div className="min-h-dvh bg-[#F2EFE9]">
      <div className="mx-auto max-w-xl px-5 py-20">
        <Wordmark />
        <h1 className="li-serif mt-8 text-3xl text-[#1C2430]">This link is not complete</h1>
        <p className="mt-4 leading-relaxed text-[#5B6472]">
          Your dashboard link is long, and email programs sometimes cut it in half or wrap it
          across two lines. If you copied it by hand, check that you have the whole thing.
        </p>
        <p className="mt-3 leading-relaxed text-[#5B6472]">
          Easier: open the email again and click the link instead of copying it. If that still
          does not work, reply to the email and we will send you a fresh one.
        </p>
        <a
          href="mailto:hello@sunbedly.com?subject=My%20dashboard%20link%20does%20not%20work"
          className="li-mono mt-8 inline-flex h-12 items-center rounded-lg border-2 border-[#1C2430] bg-[#1C2430] px-6 text-[13px] font-medium text-[#F2EFE9] transition-colors hover:border-[#2C4FD6] hover:bg-[#2C4FD6]"
        >
          Ask us for a new link
        </a>
      </div>
      <Footer />
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === "confirmed"
      ? "bg-[#2C4FD6] text-[#F2EFE9]"
      : status === "declined" || status === "cancelled"
        ? "bg-[#1C2430]/10 text-[#5B6472]"
        : "bg-[#E7E2D6] text-[#1C2430]";
  return (
    <span className={`li-mono rounded-md px-2 py-1 text-[10px] uppercase tracking-wider ${tone}`}>
      {status}
    </span>
  );
}

function contactHref(contact: string): string | null {
  const trimmed = contact.trim();
  if (trimmed.includes("@")) return `mailto:${trimmed}`;
  const digits = trimmed.replace(/[^0-9+]/g, "");
  return digits.length >= 8 ? `tel:${digits}` : null;
}

function BookingRow({
  title,
  sub,
  contact,
  status,
  busy,
  onConfirm,
  onDecline,
}: {
  title: string;
  sub: string;
  contact: string;
  status: string;
  busy: boolean;
  origin?: string;
  onConfirm: () => void;
  onDecline: () => void;
}) {
  const { t } = useI18n();
  const href = contactHref(contact);
  return (
    <div className="flex flex-col gap-3 border-b border-[#1C2430]/10 py-4 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="li-display font-bold tracking-tight text-[#1C2430]">{title}</p>
          <StatusPill status={status} />
          {origin ? (
            <span className="li-mono text-[10px] uppercase tracking-wider text-[#5B6472]">
              {origin}
            </span>
          ) : null}
        </div>
        <p className="mt-0.5 text-sm text-[#5B6472]">{sub}</p>
        {href ? (
          <a
            href={href}
            className="li-mono mt-1 inline-block text-[12px] text-[#2C4FD6] underline underline-offset-4"
          >
            {contact}
          </a>
        ) : (
          <p className="li-mono mt-1 text-[12px] text-[#5B6472]">{contact}</p>
        )}
      </div>
      <div className="flex shrink-0 gap-2">
        <button
          type="button"
          disabled={busy || status === "confirmed"}
          onClick={onConfirm}
          className="li-mono rounded-lg bg-[#1C2430] px-4 py-2 text-[12px] text-[#F2EFE9] transition-colors hover:bg-[#2C4FD6] disabled:opacity-40"
        >
          {t("mg_confirm")}
        </button>
        <button
          type="button"
          disabled={busy || status === "declined"}
          onClick={onDecline}
          className="li-mono rounded-lg border border-[#1C2430]/25 px-4 py-2 text-[12px] text-[#1C2430] transition-colors hover:border-[#2C4FD6] disabled:opacity-40"
        >
          {t("mg_decline")}
        </button>
      </div>
    </div>
  );
}


// Logging a booking the club took itself. Five fields, one of them optional,
// because this is filled in on a phone with one hand while somebody waits.
function ClubBookingForm({ token, onSaved }: { token: string; onSaved: () => void }) {
  const { t } = useI18n();
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ name: "", contact: "", date: today, beds: 2, note: "" });
  const [state, setState] = useState<"idle" | "saving" | "done">("idle");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setState("saving");
    const res = await addClubBooking({
      data: {
        token,
        name: f.name.trim(),
        contact: f.contact.trim() || undefined,
        date: f.date,
        beds: Number(f.beds) || 1,
        note: f.note.trim() || undefined,
      },
    });
    if (res.ok) {
      setState("done");
      setF({ name: "", contact: "", date: today, beds: 2, note: "" });
      onSaved();
      setTimeout(() => setState("idle"), 2500);
    } else {
      setState("idle");
    }
  };

  const field =
    "li-display h-12 w-full rounded-lg border border-[#1C2430]/15 bg-white px-3 text-[16px] text-[#1C2430] focus:border-[#2C4FD6] focus:outline-none";
  const label = "li-mono mb-1 block text-[11px] uppercase tracking-wider text-[#5B6472]";

  return (
    <form onSubmit={submit} className="mt-5 rounded-xl border border-[#1C2430]/10 bg-white/70 p-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="bk-name" className={label}>{t("bk_name")}</label>
          <input id="bk-name" required value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })} className={field} />
        </div>
        <div>
          <label htmlFor="bk-contact" className={label}>{t("bk_contact")}</label>
          <input id="bk-contact" value={f.contact} inputMode="tel"
            onChange={(e) => setF({ ...f, contact: e.target.value })} className={field} />
        </div>
        <div>
          <label htmlFor="bk-date" className={label}>{t("bk_date")}</label>
          <input id="bk-date" type="date" required value={f.date}
            onChange={(e) => setF({ ...f, date: e.target.value })} className={field} />
        </div>
        <div>
          <label htmlFor="bk-beds" className={label}>{t("bk_beds")}</label>
          <input id="bk-beds" type="number" min={1} max={60} required value={f.beds}
            onChange={(e) => setF({ ...f, beds: Number(e.target.value) })} className={field} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="bk-note" className={label}>{t("bk_note")}</label>
          <input id="bk-note" value={f.note}
            onChange={(e) => setF({ ...f, note: e.target.value })} className={field} />
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button type="submit" disabled={state === "saving"}
          className="li-display h-12 rounded-lg border-2 border-[#1C2430] bg-[#1C2430] px-6 text-[15px] font-bold text-[#F2EFE9] transition-colors hover:border-[#2C4FD6] hover:bg-[#2C4FD6] disabled:opacity-60">
          {t("bk_add")}
        </button>
        {state === "done" ? (
          <span className="li-mono text-[12px] text-[#2C4FD6]">{t("bk_saved")}</span>
        ) : null}
      </div>
    </form>
  );
}

function ManagePage() {
  const { t, lang } = useI18n();
  const { token } = Route.useParams();
  const router = useRouter();
  const data = Route.useLoaderData();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [blockDate, setBlockDate] = useState("");
  const [blockNote, setBlockNote] = useState("");

  const club = data.club;
  const [form, setForm] = useState({
    price_from: club.price_from?.toString() ?? "",
    price_unit: club.price_unit ?? "",
    phone: club.phone ?? "",
    email: club.email ?? "",
    website: club.website ?? "",
  });

  const refresh = () => {
    // Router may be unavailable during SSR; a plain reload is a fine fallback
    // and this only ever runs from a click.
    if (typeof window !== "undefined") window.location.reload();
  };

  const markRequest = async (r: ManageRequest, status: "confirmed" | "declined") => {
    setBusyId(`req-${r.id}`);
    await setRequestStatus({ data: { token, id: r.id, status } });
    setBusyId(null);
    refresh();
  };

  const markReservation = async (r: ManageReservation, status: "confirmed" | "declined") => {
    setBusyId(`res-${r.id}`);
    await setReservationStatus({ data: { token, id: r.id, status } });
    setBusyId(null);
    refresh();
  };

  const addBlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!blockDate) return;
    setBusyId("block");
    await addAvailabilityBlock({
      data: { token, date: blockDate, scope: "day", spotId: null, note: blockNote.trim() || null },
    });
    setBusyId(null);
    refresh();
  };

  const dropBlock = async (id: number) => {
    setBusyId(`blk-${id}`);
    await removeAvailabilityBlock({ data: { token, id } });
    setBusyId(null);
    refresh();
  };

  const saveDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusyId("details");
    await updateClubDetails({
      data: {
        token,
        price_from: form.price_from.trim() === "" ? null : Number(form.price_from),
        price_unit: form.price_unit.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        website: form.website.trim() || null,
      },
    });
    setBusyId(null);
    setSaved(true);
  };

  const openRequests = data.requests.filter((r) => r.status === "new").length;
  const openReservations = data.reservations.filter((r) => r.status === "pending").length;

  return (
    <div className="min-h-dvh bg-[#F2EFE9]">
      <header className="border-b border-[#1C2430]/10 bg-[#F2EFE9]">
        <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-5">
          <Wordmark />
          <a
            href={`/club/${club.slug}`}
            className="li-mono text-[12px] text-[#1C2430] underline decoration-[#2C4FD6] underline-offset-4"
          >
            {t("mg_view_public")}
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-10">
        <p className="li-mono text-[11px] uppercase tracking-widest text-[#2C4FD6]">
          {t("mg_title")}
        </p>
        {/* The owner lands in the language of their own country, but a beach in
            Italy may well be run by someone who reads German. The choice is
            theirs and it lives in the URL, so forwarding the link keeps it. */}
        <div className="li-mono mt-2 flex flex-wrap items-center gap-2 text-[11px]">
          <span className="text-[#5B6472]">{t("mg_lang")}:</span>
          {LANGS.map((l) => (
            <a
              key={l.code}
              href={`?lang=${l.code}`}
              className={
                lang === l.code
                  ? "text-[#2C4FD6] underline underline-offset-4"
                  : "text-[#5B6472] hover:text-[#1C2430]"
              }
            >
              {l.label}
            </a>
          ))}
        </div>
        <h1 className="li-display mt-2 text-4xl font-black tracking-tighter text-[#1C2430] md:text-5xl">
          {club.name}
        </h1>
        <p className="mt-2 text-[#5B6472]">
          {club.town}, {club.region_label}
        </p>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          {[
            { n: openRequests, l: "requests waiting" },
            { n: openReservations, l: "bed reservations waiting" },
            { n: data.photos.length, l: "photos uploaded" },
          ].map((s) => (
            <div key={s.l} className="rounded-xl border border-[#1C2430]/10 bg-white/60 p-4">
              <p className="li-display text-3xl font-black tracking-tighter text-[#2C4FD6]">{s.n}</p>
              <p className="li-mono mt-1 text-[11px] uppercase tracking-widest text-[#5B6472]">
                {s.l}
              </p>
            </div>
          ))}
        </div>

        {/* Bookings */}
        <section className="mt-12">
          <h2 className="li-display text-2xl font-black tracking-tighter text-[#1C2430]">
            {t("mg_requests")}
          </h2>
          <p className="mt-1 text-sm text-[#5B6472]">
            {t("mg_requests_sub")}
          </p>

          <h3 className="li-display mt-6 text-base font-bold tracking-tight text-[#1C2430]">
            {t("bk_title")}
          </h3>
          <p className="mt-1 max-w-[62ch] text-sm leading-relaxed text-[#5B6472]">
            {t("bk_sub")}
          </p>
          <ClubBookingForm token={token} onSaved={() => router.invalidate()} />

          <div className="mt-6 rounded-2xl border border-[#1C2430]/10 bg-white/60 px-5">
            {data.requests.length === 0 ? (
              <p className="py-8 text-center text-sm text-[#5B6472]">
                {t("mg_no_requests")}
              </p>
            ) : (
              data.requests.map((r) => (
                <BookingRow
                  key={r.id}
                  title={`${r.name} · ${r.party_size} ${r.party_size === 1 ? "person" : "people"}`}
                  origin={r.source === "club" ? t("bk_by_club") : t("bk_by_site")}
                  sub={`Visiting ${r.visit_date}${r.message ? ` — "${r.message}"` : ""}`}
                  contact={r.contact}
                  status={r.status}
                  busy={busyId === `req-${r.id}`}
                  onConfirm={() => void markRequest(r, "confirmed")}
                  onDecline={() => void markRequest(r, "declined")}
                />
              ))
            )}
          </div>
        </section>

        {/* Spot reservations */}
        {data.reservations.length > 0 ? (
          <section className="mt-12">
            <h2 className="li-display text-2xl font-black tracking-tighter text-[#1C2430]">
              {t("mg_reservations")}
            </h2>
            <p className="mt-1 text-sm text-[#5B6472]">
              {t("mg_res_sub")}
            </p>
            <div className="mt-4 rounded-2xl border border-[#1C2430]/10 bg-white/60 px-5">
              {data.reservations.map((r) => (
                <BookingRow
                  key={r.id}
                  title={`Spot ${r.spot_id} · ${r.name}`}
                  sub={`Visiting ${r.visit_date} · group ${r.group_code}`}
                  contact={r.contact}
                  status={r.status}
                  busy={busyId === `res-${r.id}`}
                  onConfirm={() => void markReservation(r, "confirmed")}
                  onDecline={() => void markReservation(r, "declined")}
                />
              ))}
            </div>
          </section>
        ) : null}

        {/* Availability */}
        <section className="mt-12">
          <h2 className="li-serif text-2xl text-[#1C2430]">{t("mg_close_dates")}</h2>
          <p className="mt-1 text-sm text-[#5B6472]">
            Days you are shut, fully booked, or privately hired. Travelers stop seeing beds for
            these dates straight away.
          </p>
          <form
            onSubmit={addBlock}
            className="mt-4 flex flex-col gap-3 rounded-2xl border border-[#1C2430]/10 bg-white/60 p-5 sm:flex-row sm:items-end"
          >
            <label className="block sm:w-52">
              <span className="li-mono text-[11px] uppercase tracking-widest text-[#5B6472]">
                Date
              </span>
              <input
                type="date"
                required
                value={blockDate}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setBlockDate(e.target.value)}
                className="li-display mt-1 h-12 w-full rounded-lg border border-[#1C2430]/20 bg-white px-3 text-[#1C2430] focus:border-[#2C4FD6] focus:outline-none"
              />
            </label>
            <label className="block flex-1">
              <span className="li-mono text-[11px] uppercase tracking-widest text-[#5B6472]">
                {t("mg_note_opt")}
              </span>
              <input
                type="text"
                value={blockNote}
                placeholder={t("mg_private")}
                onChange={(e) => setBlockNote(e.target.value)}
                className="li-display mt-1 h-12 w-full rounded-lg border border-[#1C2430]/20 bg-white px-3 text-[#1C2430] focus:border-[#2C4FD6] focus:outline-none"
              />
            </label>
            <button
              type="submit"
              disabled={busyId === "block" || !blockDate}
              className="li-mono h-12 shrink-0 rounded-lg bg-[#1C2430] px-6 text-[13px] text-[#F2EFE9] transition-colors hover:bg-[#2C4FD6] disabled:opacity-40"
            >
              {busyId === "block" ? "Saving..." : "Close this date"}
            </button>
          </form>

          <div className="mt-4 rounded-2xl border border-[#1C2430]/10 bg-white/60 px-5">
            {data.blocks.length === 0 ? (
              <p className="py-8 text-center text-sm text-[#5B6472]">
                {t("mg_no_closed")}
              </p>
            ) : (
              data.blocks.map((b) => (
                <div
                  key={b.id}
                  className="flex items-center justify-between border-b border-[#1C2430]/10 py-4 last:border-b-0"
                >
                  <div>
                    <p className="li-display font-bold tracking-tight text-[#1C2430]">
                      {b.block_date}
                      {b.scope === "spot" && b.spot_id ? ` · spot ${b.spot_id}` : " · whole day"}
                    </p>
                    {b.note ? <p className="mt-0.5 text-sm text-[#5B6472]">{b.note}</p> : null}
                  </div>
                  <button
                    type="button"
                    disabled={busyId === `blk-${b.id}`}
                    onClick={() => void dropBlock(b.id)}
                    className="li-mono rounded-lg border border-[#1C2430]/25 px-4 py-2 text-[12px] text-[#1C2430] transition-colors hover:border-[#2C4FD6] disabled:opacity-40"
                  >
                    {t("mg_reopen")}
                  </button>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Photos */}
        <section className="mt-12">
          <h2 className="li-display text-2xl font-black tracking-tighter text-[#1C2430]">
            {t("mg_photos")}
          </h2>
          <p className="mt-1 text-sm text-[#5B6472]">
            Your own photos only, please. A page with a real photo of your beach gets far more
            requests than one without.
          </p>
          <div className="mt-4">
            <PhotoUpload clubSlug={club.slug} manageToken={token} />
          </div>
        </section>

        {/* Details */}
        <section className="mt-12">
          <h2 className="li-display text-2xl font-black tracking-tighter text-[#1C2430]">
            {t("mg_prices")}
          </h2>
          <p className="mt-1 text-sm text-[#5B6472]">
            What you put here shows on your public page straight away, and marks it as confirmed by
            you.
          </p>
          <form
            onSubmit={saveDetails}
            className="mt-4 grid gap-4 rounded-2xl border border-[#1C2430]/10 bg-white/60 p-5 sm:grid-cols-2"
          >
            {[
              { k: "price_from", l: "Price from (EUR)", ph: "30", type: "number" },
              { k: "price_unit", l: "What that covers", ph: "2 beds + parasol", type: "text" },
              { k: "phone", l: "Phone / WhatsApp", ph: "+39 ...", type: "text" },
              { k: "email", l: "Email", ph: "info@...", type: "email" },
              { k: "website", l: "Website", ph: "https://...", type: "url" },
            ].map((f) => (
              <label key={f.k} className="block">
                <span className="li-mono text-[11px] uppercase tracking-widest text-[#5B6472]">
                  {f.l}
                </span>
                <input
                  type={f.type}
                  value={form[f.k as keyof typeof form]}
                  placeholder={f.ph}
                  onChange={(e) => {
                    setSaved(false);
                    setForm({ ...form, [f.k]: e.target.value });
                  }}
                  className="li-display mt-1 h-12 w-full rounded-lg border border-[#1C2430]/20 bg-white px-3 text-[#1C2430] focus:border-[#2C4FD6] focus:outline-none"
                />
              </label>
            ))}
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={busyId === "details"}
                className="li-mono rounded-lg bg-[#1C2430] px-6 py-3 text-[13px] text-[#F2EFE9] transition-colors hover:bg-[#2C4FD6] disabled:opacity-50"
              >
                {busyId === "details" ? "Saving..." : "Save"}
              </button>
              {saved ? (
                <span className="li-mono ml-3 text-[12px] text-[#2C4FD6]">{t("mg_saved")}</span>
              ) : null}
            </div>
          </form>
        </section>

        {/* The badge: a voluntary, verifiable link from the club's own site.
            For the club it signals travellers can find and request them here;
            for sunbedly every placed badge is exactly the kind of external
            proof of being a real, used service that no directory can fake. */}
        <section className="mt-12">
          <h2 className="li-display text-2xl font-black tracking-tighter text-[#1C2430]">
            Show it on your own website
          </h2>
          <p className="mt-1 max-w-[60ch] text-sm text-[#5B6472]">
            Paste this snippet on your site to show guests they can find you here. It links
            straight to your own page.
          </p>
          <div className="mt-4 flex flex-wrap items-start gap-5 rounded-2xl border border-[#1C2430]/10 bg-white/60 p-5">
            <img src="/assets/listed-badge.svg" alt="Listed on sunbedly" width={210} height={56} />
            <textarea
              readOnly
              rows={3}
              onFocus={(e) => e.currentTarget.select()}
              value={`<a href="https://sunbedly.com/club/${club.slug}?utm_source=club-badge"><img src="https://sunbedly.com/assets/listed-badge.svg" alt="Listed on sunbedly" width="210" height="56" loading="lazy"></a>`}
              className="li-mono min-w-[260px] flex-1 rounded-lg border border-[#1C2430]/20 bg-white p-3 text-[11px] leading-relaxed text-[#1C2430] focus:border-[#2C4FD6] focus:outline-none"
            />
          </div>
        </section>

        <p className="li-mono mt-12 rounded-xl border border-[#1C2430]/10 bg-[#E7E2D6] p-4 text-[12px] leading-relaxed text-[#5B6472]">
          Keep this link private, it is the key to your dashboard. Bookmark it, or save it in your
          WhatsApp saved messages so you always have it on your phone.
        </p>

        {/* The five things an owner asks before they trust a page they did not
            create. Answering them here, in their language, is cheaper than
            answering them one email at a time. */}
        <section className="mt-14 rounded-2xl border border-[#1C2430]/10 bg-white/60 p-6">
          <h2 className="li-serif text-2xl text-[#1C2430]">{t("ofaq_title")}</h2>
          <dl className="mt-5 divide-y divide-[#1C2430]/10">
            {[1, 2, 3, 4, 5].map((n) => (
              <div key={n} className="py-4">
                <dt className="li-display text-base font-bold tracking-tight text-[#1C2430]">
                  {t(`ofaq_q${n}`)}
                </dt>
                <dd className="mt-1.5 max-w-[70ch] text-sm leading-relaxed text-[#5B6472]">
                  {t(`ofaq_a${n}`)}
                </dd>
              </div>
            ))}
          </dl>
          <a
            href={`mailto:hello@sunbedly.com?subject=${encodeURIComponent(data.club.name)}`}
            className="li-mono mt-4 inline-block text-[12px] text-[#2C4FD6] underline underline-offset-4"
          >
            {t("ofaq_contact")}
          </a>
        </section>
      </main>
      <Footer />
    </div>
  );
}
